import { useCallback, useEffect, useState } from "react";

import { useDispatch } from "react-redux";

import { translate } from "app/filters/i18nFilter";
import type { Competitor } from "app/pages/sales-intelligence/modules/ai-agent/types/meeting";
import useChatQuery from "app/pages/sales-intelligence/modules/ai-agent/queries/useChatQuery";
import ParseJSONService from "app/pages/sales-intelligence/modules/ai-agent/services/jsonParser/jsonParserService";
import type { ParsedAIEmailPreview } from "app/pages/sales-intelligence/modules/ai-agent/services/jsonParser/types";
import AIAgentTrackingService from "app/pages/sales-intelligence/modules/ai-agent/services/tracking/trackingService";
import { addAgentMessage, addHumanMessage, setMessages } from "app/pages/sales-intelligence/modules/ai-agent/store";
import { SUGGESTION_TEXTS } from "app/pages/sales-intelligence/pages/ai-agent/constants";
import salesApiService from "app/pages/sales-intelligence/services/salesApiService";

type UseEmailRefineChatArgs = {
    threadId: string;
    onAddVersion: (v: ParsedAIEmailPreview) => void;
    onScrollUserMessageToTop: (bottomOffset?: number) => void;
    onScrollAiResponseToBottom: VoidFunction;
    onShowCompetitors: VoidFunction;
    onSetEmailPreviews: (previews: ParsedAIEmailPreview[]) => void;
    onShowPreviews: VoidFunction;
};

const LOADING_MESSAGE_HEIGHT = 90;

type UseEmailRefineChatReturn = {
    localText: string;
    onTextChange: (value?: string) => void;
    isLoadingChat: boolean;
    onSubmitChat: VoidFunction;
    onStopChat: () => Promise<void>;
    competitors: Competitor[];
    onCompetitorsChange: (competitors: Competitor[]) => void;
    onRegenerateVisuals: VoidFunction;
};

export const useEmailRefineChat = ({
    threadId,
    onAddVersion,
    onScrollUserMessageToTop,
    onScrollAiResponseToBottom,
    onShowCompetitors,
    onSetEmailPreviews,
    onShowPreviews,
}: UseEmailRefineChatArgs): UseEmailRefineChatReturn => {
    const dispatch = useDispatch();
    const [localText, setLocalText] = useState("");
    const [activeQuestion, setActiveQuestion] = useState("");
    const [questionId, setQuestionCount] = useState(0);
    const [competitors, setCompetitors] = useState<Competitor[]>([]);

    const {
        data,
        isLoading: isLoadingChat,
        stop,
        messageId,
        messageType,
    } = useChatQuery({
        message: activeQuestion,
        threadId,
        questionId,
        fetcher: salesApiService.aiAgent.email.postAIEmailAgentMessage,
    });

    const onTextChange = useCallback((value?: string) => {
        if (value === "\n") {
            return;
        }
        setLocalText(value ?? "");
    }, []);

    const clearText = useCallback(() => setLocalText(""), []);

    const handleSubmitMessage = useCallback(
        (message: string) => {
            if (!message) {
                return;
            }
            dispatch(addHumanMessage(message));
            setActiveQuestion(message);
            setQuestionCount((c) => c + 1);
            clearText();
            onScrollUserMessageToTop(questionId === 0 ? LOADING_MESSAGE_HEIGHT : LOADING_MESSAGE_HEIGHT + 16 * 2);
            AIAgentTrackingService.email.trackSendMessage(message);
        },
        [questionId, onScrollUserMessageToTop],
    );

    const handleSubmitChat = useCallback(() => {
        const message = localText.trim();
        handleSubmitMessage(message);
    }, [handleSubmitMessage, localText]);

    const handleRegenerateVisuals = useCallback(() => {
        const message = translate(SUGGESTION_TEXTS[0]);
        handleSubmitMessage(message);
        onSetEmailPreviews([]);
    }, [handleSubmitMessage]);

    useEffect(() => {
        if (data && messageId) {
            const isNewVersionMessage =
                messageType === "email" || (messageType !== "email_previews" && ParseJSONService.isLikeEmailJSON(data));
            const isEmailPreviewsMessage =
                messageType === "email_previews" ||
                (messageType === "ai" && ParseJSONService.isLikeEmailPreviewJSON(data));

            if (isNewVersionMessage) {
                const parsedEmail = ParseJSONService.parseEmail(data);
                if (parsedEmail.body && parsedEmail.subject) {
                    const { cleaned, json, index } = ParseJSONService.extractEmbeddedJSON(parsedEmail.body);
                    const version = {
                        subject: parsedEmail.subject,
                        body: cleaned,
                        chartJson: json as Record<string, unknown>,
                        chartAnchorIndex: index,
                    };
                    onAddVersion(version);
                    onScrollAiResponseToBottom();
                }
            } else if (isEmailPreviewsMessage) {
                const previews = ParseJSONService.parseAIEmailPreviews(data);
                const filteredPreviews = previews.filter((p) => p.subject && p.body);
                if (filteredPreviews.length > 0) {
                    onSetEmailPreviews(filteredPreviews);
                    onShowPreviews();
                }
            } else if (messageType === "competitors") {
                const competitorsData = ParseJSONService.parseCompetitors(data);
                onShowCompetitors();
                setCompetitors(competitorsData);
            } else if (messageType === "ai") {
                dispatch(addAgentMessage({ content: data, id: messageId }));
                onScrollAiResponseToBottom();
            }
            setActiveQuestion("");
        }
    }, [data]);

    useEffect(() => {
        return () => {
            dispatch(setMessages([]));
        };
    }, []);

    return {
        localText,
        onTextChange,
        isLoadingChat,
        onSubmitChat: handleSubmitChat,
        onStopChat: stop,
        competitors,
        onCompetitorsChange: setCompetitors,
        onRegenerateVisuals: handleRegenerateVisuals,
    };
};
